import React, { useState } from 'react';
import { useReactionLeaderboard, LeaderboardFilter } from '@/hooks/useReactionLeaderboard';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Trophy } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

const FILTER_OPTIONS: { id: LeaderboardFilter; label: string; emoji: string }[] = [
  { id: 'trophy', label: 'Elite', emoji: '🏆' },
  { id: 'grind', label: 'Grind', emoji: '💪' },
  { id: 'brain', label: 'IQ', emoji: '🧠' },
  { id: 'clutch', label: 'Clutch', emoji: '🎯' },
  { id: 'week', label: 'This Week', emoji: '📅' },
];

interface ReactionLeaderboardProps {
  className?: string;
}

const ReactionLeaderboard: React.FC<ReactionLeaderboardProps> = ({ className }) => {
  const [filter, setFilter] = useState<LeaderboardFilter>('trophy');
  const { entries, isLoading } = useReactionLeaderboard(filter);
  
  return (
    <Card className={cn('', className)}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Trophy className="h-5 w-5 text-primary" />
          Reaction Leaders
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-1 mb-4">
          {FILTER_OPTIONS.map((option) => (
            <button
              key={option.id}
              onClick={() => setFilter(option.id)}
              className={cn(
                'px-2 py-1 text-xs font-medium rounded-md transition-colors',
                filter === option.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
            >
              <span className="mr-1">{option.emoji}</span>
              {option.label}
            </button>
          ))}
        </div>
        
        {/* Leaderboard List */}
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground text-sm">
            No reactions yet this period
          </div>
        ) : (
          <div className="space-y-2">
            {entries.map((entry, index) => (
              <Link
                key={entry.profile_id}
                to={`/athletes/${entry.username || entry.profile_id}`}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-accent transition-colors"
              >
                {/* Rank */}
                <span className={cn(
                  'w-6 h-6 flex items-center justify-center text-xs font-bold rounded-full',
                  index === 0 && 'bg-yellow-500 text-white',
                  index === 1 && 'bg-gray-400 text-white',
                  index === 2 && 'bg-amber-600 text-white',
                  index > 2 && 'bg-muted text-muted-foreground'
                )}>
                  {index + 1}
                </span>
                
                {/* Avatar */}
                <Avatar className="h-8 w-8">
                  <AvatarImage src={entry.profile_photo_url || undefined} />
                  <AvatarFallback className="text-xs">
                    {entry.name?.charAt(0) || '?'}
                  </AvatarFallback>
                </Avatar>
                
                {/* Name */}
                <span className="flex-1 text-sm font-medium truncate">
                  {entry.name}
                </span>
                
                {/* Count */}
                <span className="text-sm font-bold text-primary">
                  {entry.count}
                </span>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReactionLeaderboard;
